import { DEFAULT_CENTER } from "@/store/mapConstants";
import { useEncryptStore } from "@/store/useEncryptStore";
import { useUserStore } from "@/store/useUserStore";
import { useRouterStore } from "@/store/router";
import { useTerritoriosStore } from "@/store/territorios";
import { useTerritorioStore } from "@/store/territorio";
import { useSalidaStore } from "@/store/useSalidaStore";
import { useTrabajoStore } from "@/store/useTrabajoStore";
import { useMapStore } from "@/store/map";
import { useRutasStore } from "@/page/rutas/useRutasStore";
import { useApi } from "@/composables/useApi";
import { useCatalogos } from "@/composables/useCatalogos";
import { notify } from "@/composables/useNotificationBus";

const newId = () => globalThis.crypto.randomUUID()

const migrateConfig = (config = {}) => {
  const toObj = (arr) =>
    (arr || []).map((item) =>
      typeof item === 'string' ? { id: newId(), name: item } : item
    )
  config.conductores = toObj(config.conductores)
  config.grupos = toObj(config.grupos)
  config.zonas = toObj(config.zonas)
  config.puntoEncuentro = (config.puntoEncuentro || []).map((p) =>
    p.id ? p : { ...p, id: newId() }
  )
  return config
}

export const useStorage = () => {
  const m = useMapStore();
  const territorios = useTerritoriosStore();
  const territorio = useTerritorioStore();
  const route = useRouterStore();
  const trabajo = useTrabajoStore();
  const salida = useSalidaStore();
  const rutasStore = useRutasStore();
  const api = useApi();
  const cat = useCatalogos();
  const crypto = useEncryptStore();
  const userStore = useUserStore();

  // Intenta migrar el blob antiguo encriptado a las nuevas tablas
  const migrateFromBlob = async () => {
    try {
      const result = await api.get("/api/data");
      if (!result?.data) return false;
      crypto.setKey(userStore.encryptionKey);
      const decrypted = await crypto.decrypt(result.data);
      const oldData = JSON.parse(decrypted);
      if (!oldData?.territorios?.length) return false;
      await api.post("/api/migrate", oldData);
      return true;
    } catch (e) {
      console.error("Error migrando datos antiguos:", e);
      return false;
    }
  };

  // Páginas que NO funcionan sin un territorio seleccionado; si no se resuelve, van a la lista
  const REQUIRE_TERRITORY = new Set([
    "formTerritorio",
    "transporteTerritorio",
    "tarjetaTerritorio",
  ]);

  // Restaura la página según la URL con la que se cargó la app (initialRoute).
  // Si la URL trae ?id=<uuid>, carga ese territorio en el store antes de mostrar la página.
  const restoreRoute = () => {
    const { initialRoute } = route;
    const target = initialRoute.page;

    if (!target || target === "salidas") {
      route.$patch({ page: "salidas" });
      return;
    }

    const id = initialRoute.params?.id;
    const terr = id ? territorios.getTerritorioById(id) : null;
    if (terr) {
      territorio.cargar(terr);
    }

    // Página por-territorio sin un territorio resoluble → volver a la lista
    if (REQUIRE_TERRITORY.has(target) && !terr) {
      route.$patch({ page: "listaTerritorio" });
      return;
    }

    route.$patch({ page: target, params: terr ? { id } : { ...initialRoute.params } });
  };

  // Vuelve a pedir todo al servidor y sobreescribe los stores con lo fresco.
  // Se usa tanto en la carga inicial (getData) como en cada cambio de página
  // dentro de la SPA, para que la navegación interna no muestre datos viejos
  // en memoria (ver watch de route.page en App.vue).
  let isRefreshing = false;
  const refreshData = async () => {
    if (isRefreshing) return;
    isRefreshing = true;
    try {
      const [statsRes, assignRes, salidaRes, configRes, gruposRes, zonasRes, conductoresRes] = await Promise.all([
        api.get("/api/territories/stats"),
        api.get("/api/assignments"),
        api.get("/api/salidas"),
        api.get("/api/config"),
        cat.getGrupos(),
        cat.getZonas(),
        cat.getConductores(),
      ]);
      const config = migrateConfig(configRes);

      const terrList = statsRes.territories || [];
      for (const t of terrList) {
        if (!t.color) t.color = { backgroundColor: "rgb(255, 255, 254)", color: "#000000" };
      }

      territorios.$patch({
        list: terrList,
        asignados: assignRes.assignments || [],
        puntoEncuentro: config.puntoEncuentro,
        congregacion: config.congregacion || null,
        grupos: gruposRes.grupos || [],
        zonas: zonasRes.zonas || [],
      });

      // Defensivo: garantiza uuid en todo territorio (estado heredado sin uuid)
      await territorios.ensureUuids();

      const defaultPunto = config.puntoEncuentro.find((p) => p.isDefault)?.latlng;
      const defaultPuntoCenter = defaultPunto ? [defaultPunto.lat, defaultPunto.lng] : null;
      m.setCenter({ center: configRes.mapCenter || defaultPuntoCenter || DEFAULT_CENTER });

      // Construir trabajo.data directamente desde las stats del servidor
      const trabajoData = {};
      for (const t of terrList) {
        const key = t.uuid;
        trabajoData[key] = {
          registros: t.stats.registros,
          manzanasTotal: t.stats.manzanasTotal,
          manzanasPendientes: t.stats.manzanasPendientes,
          manzanasPendientesTotal: t.stats.manzanasPendientesTotal,
          vecesCompletado: t.stats.vecesCompletado,
          enProgreso: t.stats.enProgreso,
          ultimaActividad: t.stats.ultimaActividad,
          ultimaCompletacion: t.stats.ultimaCompletacion,
        };
      }
      trabajo.$patch({ data: trabajoData });
      trabajo.syncHistory();

      rutasStore.$patch({ list: configRes.rutas || [] });

      salida.$patch({
        horarios: config.horarios || ["07:30", "09:00", "17:00"],
        conductores: conductoresRes.conductores || [],
        list: { data: salidaRes.salidas || [] },
        history: salidaRes.history || [],
      });

      return terrList;
    } finally {
      isRefreshing = false;
    }
  };

  const getData = async () => {
    // La clave sigue siendo necesaria para desencriptar shared_docs (links de salidas)
    crypto.setKey(userStore.encryptionKey);
    try {
      const statsRes = await api.get("/api/territories/stats");

      // Si no hay territorios, intentar migrar desde el blob antiguo
      if (!statsRes.territories?.length) {
        const migrated = await migrateFromBlob();
        if (migrated) return getData();

        // Grupos: usuario nuevo sin catálogo, sembrar los 5 grupos por defecto
        // como filas reales editables por id antes de mostrar la app.
        const gruposRes = await cat.getGrupos();
        if (!gruposRes.grupos?.length) {
          for (const name of ["1", "2", "3", "4", "5"]) {
            await cat.createGrupo({ name });
          }
        }

        await refreshData();
        await crypto.setReady();
        route.$patch({ page: "parametros" });
        return;
      }

      await refreshData();
      await crypto.setReady();
      restoreRoute();
    } catch (e) {
      console.error("Error al obtener datos:", e);
      notify.error("Error al cargar datos", "Recarga la página e intenta nuevamente.");
    }
  };

  const saveAssignments = () =>
    api.put('/api/assignments', { assignments: territorios.asignados })

  // Campos que aún viven en el blob app_config. grupos/zonas/conductores ya NO están
  // acá: se guardan por-registro (useCatalogos). Se puede guardar sólo una sección
  // pasando `fields` (p. ej. saveConfig(['horarios'])) para no pisar el resto.
  const CONFIG_FIELDS = {
    horarios: () => salida.horarios,
    puntoEncuentro: () => territorios.puntoEncuentro,
    congregacion: () => territorios.congregacion,
    mapCenter: () => m.center,
    rutas: () => rutasStore.list,
  }
  const saveConfig = (fields) => {
    const keys = fields && fields.length ? fields : Object.keys(CONFIG_FIELDS)
    const body = {}
    for (const k of keys) if (CONFIG_FIELDS[k]) body[k] = CONFIG_FIELDS[k]()
    return api.put('/api/config', body)
  }

  return { getData, refreshData, saveAssignments, saveConfig, ready: crypto.ready };
};
